import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Image,
  Text,
  Badge,
  Group,
  ActionIcon,
  SimpleGrid,
  Modal,
  TextInput,
  Stack,
  Menu,
  Box,
  AspectRatio,
  Button,
  Tooltip,
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconCopy,
  IconDots,
  IconPlus,
  IconLayoutGrid,
  IconLayoutList,
} from '@tabler/icons-react';
import { DatabaseItem, Database } from '../../../types/models/Database';
import { PropertyChip } from '../common/PropertyChip';
import { resolveCover } from '../database/coverResolver';
import { useVibeCardState } from '../cockpit/VibeCard/useVibeCardState';
import '../common/PeekContainer.css';

interface GalleryViewProps {
  database: Database;
  items: DatabaseItem[];
  coverPropertyId?: string;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemCreate?: (item: DatabaseItem) => void;
  /**
   * F 139 — open the HOST's create flow.
   *
   * `onItemCreate` is the wrong verb for "start creating": it reports an item
   * that already exists. The host's "New" button uses its own state
   * (`isNewItemOpen` in DatabaseView); this view's placeholder used its own
   * (`isAddingItem` here). Two modals, one button — which is why the
   * placeholder did not do the same thing as "New".
   *
   * When supplied, the placeholder and the mobile button open THIS flow. When
   * omitted, the view falls back to its own modal, so GalleryView stays
   * usable standalone.
   */
  onItemCreateRequest?: () => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (item: DatabaseItem) => void;
  onPropertyChange?: (itemId: string, propertyId: string, value: any) => void;
}

// F 85 — G-1: prop type now exported as part of the public view API.
export type { GalleryViewProps };

const GRADIENT_COVERS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
  'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
  'linear-gradient(135deg, #cd9cf2 0%, #f6f3ff 100%)',
  'linear-gradient(135deg, #fddb92 0%, #d1fdff 100%)',
];

export const GalleryView: React.FC<GalleryViewProps> = ({
  database,
  items,
  coverPropertyId,
  onItemSelect,
  onItemCreate,
  onItemCreateRequest,
  onItemUpdate,
  onItemDelete,
  onItemDuplicate,
  onPropertyChange,
}) => {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [editingItem, setEditingItem] = useState<DatabaseItem | null>(null);

  // Detect narrow viewports so the cover aspect ratio doesn't dominate the screen
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Density: 'compact' = 2 cols + minimal card body, 'comfortable' = 2 cols + full.
  // Persisted per-user in localStorage. Used to control the SimpleGrid breakpoint
  // and whether the card body shows the properties row.
  const [density, setDensity] = useState<'compact' | 'comfortable'>(() => {
    if (typeof window === 'undefined') return 'compact';
    return (localStorage.getItem('freshcards:galleryDensity') as 'compact' | 'comfortable') || 'compact';
  });
  useEffect(() => {
    localStorage.setItem('freshcards:galleryDensity', density);
  }, [density]);
  const toggleDensity = () => setDensity(d => d === 'compact' ? 'comfortable' : 'compact');

  const getGradientCover = (itemId: string): string => {
    const hash = itemId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return GRADIENT_COVERS[hash % GRADIENT_COVERS.length];
  };

  const handleCreateItem = () => {
    if (!newItemTitle.trim()) return;
    const newItem: DatabaseItem = {
      id: `item_${Date.now()}`,
      databaseId: database.id,
      title: newItemTitle,
      properties: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    onItemCreate?.(newItem);
    setIsAddingItem(false);
    setNewItemTitle('');
  };

  const handleDeleteItem = (itemId: string) => {
    if (confirm('Delete this item?')) {
      onItemDelete?.(itemId);
    }
  };

  // F 139 — the ONE create path the placeholder, the mobile button and the
  // host's "New" all resolve to.
  const openCreate = useCallback(() => {
    if (onItemCreateRequest) onItemCreateRequest();
    else setIsAddingItem(true);
  }, [onItemCreateRequest]);

  const handlePropertyChange = (itemId: string, propertyId: string, value: any) => {
    onPropertyChange?.(itemId, propertyId, value);
  };

  return (
    <div className="gallery-view-wrapper" style={{ width: '100%' }}>
      <Box p={{ base: 'sm', sm: 'md', md: 'lg' }}>
          {/* Tiny density toggle — pinned to top-right of the grid, only
              takes one row of space. Lets users switch between compact
              (3-6 cols, small text) and comfortable (2-3 cols, full text). */}
          <Group justify="flex-end" mb="xs">
            <Tooltip label={density === 'compact' ? 'Switch to comfortable view' : 'Switch to compact view'}>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={toggleDensity}
                title="Toggle density"
                aria-label="Toggle density"
              >
                {density === 'compact' ? <IconLayoutGrid size={16} /> : <IconLayoutList size={16} />}
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* Mobile: full-width Add item button above the grid. */}
          {isMobile && (
            <Button
              variant="light"
              color="blue"
              size="sm"
              leftSection={<IconPlus size={14} />}
              onClick={openCreate}
              data-fc-add-item-mobile=""
              style={{ width: '100%', marginBottom: 4 }}
            >
              Add item
            </Button>
          )}

          <SimpleGrid
            cols={density === 'compact'
              ? { base: 2, xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }
              : { base: 1, sm: 2, md: 3, lg: 3, xl: 4 }}
            spacing={density === 'compact' ? { base: 'sm', sm: 'md' } : 'lg'}
            className="fc-stagger"
          >
            {!isMobile && (
              <Card
                p="lg"
                radius="md"
                className="fc-card fc-card--add"
                // F 137 — the placeholder was a clickable-looking div with an
                // onClick and nothing else: no role, no tabIndex, no key
                // handler. It read as an affordance and was not one for keyboard
                // or assistive tech. The mobile variant above is a real <Button>
                // and always worked, which is exactly why the desktop one looked
                // broken by comparison.
                role="button"
                tabIndex={0}
                aria-label="Add item"
                data-fc-card-add=""
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    e.preventDefault();
                    openCreate();
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 220,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  border: '1px dashed var(--fc-border-secondary)',
                  boxShadow: 'none',
                }}
                onClick={openCreate}
              >
                <Stack align="center" gap="md">
                  <IconPlus size={32} />
                  <Text c="dimmed" size="sm" fw={500}>
                    Add item
                  </Text>
                </Stack>
              </Card>
            )}

          {items.map(item => {
            // Resolve the cover: prefer a 'cover' property URL, then a
            // schema-declared 'image' property, then a deterministic gradient.
            const coverUrl = resolveCover(item, database);
            // The coverUrl may be a remote HTTP URL, a data URL, or a CSS gradient.
            // We render an <img> for remote URLs (so it can be lazy + has
            // alt text + a fallback) and a Box with gradient for inline.
            const isRemote = coverUrl.startsWith('http');
            return (
              <Card
                key={item.id}
                p={0}
                radius="md"
                className="fc-card fc-card--gallery"
                data-card-id={item.id}
                // F 139 DEFECT B — the card advertised itself as clickable
                // (cursor:pointer + a hover lift) but carried NO click handler.
                // The only handler was on the title <Text>, so clicking the
                // image, the date or the card body did nothing at all. That is
                // why the cursor changed and no view opened. The whole tile is
                // now the control, matching List (row click) and Kanban (card
                // click), which both route to the same onItemSelect.
                data-fc-gallery-card={item.id}
                role="button"
                tabIndex={0}
                aria-label={`Open ${item.title}`}
                onClick={() => onItemSelect?.(item)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    e.preventDefault();
                    onItemSelect?.(item);
                  }
                }}
                style={{
                  // F 367: this inline background was `var(--mantine-color-default)`,
                  // which follows MANTINE's colour scheme — not FCC's theme. It is
                  // what actually painted these cards (an inline style beats the
                  // `.fc-card--gallery` rule), so the card's appearance was
                  // decided by a variable the FCC theme switch never touches.
                  // Use the FCC tokens so the card tracks --fc-* in both schemes.
                  backgroundColor: 'var(--fc-bg-secondary)',
                  color: 'var(--fc-text-primary)',
                  borderColor: 'var(--fc-border-secondary)',
                  boxShadow: 'var(--fc-card-shadow)',
                  cursor: 'pointer',
                  transition: 'transform 150ms ease, box-shadow 150ms ease',
                  maxWidth: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = 'var(--fc-shadow-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--fc-card-shadow)';
                }}
              >
                <div style={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit', overflow: 'hidden' }}>
                <AspectRatio
                  ratio={isMobile ? 16 / 10 : 16 / 8}
                  style={{
                    // Always have a gradient as fallback so a failed image
                    // never shows a dark slab.
                    background: isRemote
                      ? `linear-gradient(135deg, #667eea, #764ba2), ${coverUrl.startsWith('http') ? `center / cover no-repeat url(${coverUrl})` : ''}`
                      : coverUrl,
                  }}
                >
                  {isRemote ? (
                    <img
                      src={coverUrl}
                      alt={item.title}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : <Box />}
                </AspectRatio>
                </div>

                <div style={{ padding: density === 'compact' ? '8px' : '12px' }}>
                  <Group justify="space-between" align="flex-start" mb={density === 'compact' ? 4 : 'xs'}>
                    <Stack gap={0} style={{ flex: 1 }}>
                      <Text
                        size={density === 'compact' ? 'xs' : 'sm'}
                        fw={600}
                        lineClamp={density === 'compact' ? 1 : 2}
                      >
                        {item.title}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {item.createdAt.toLocaleDateString()}
                      </Text>
                    </Stack>
                    {/* F 201 B — the 3-dot rendered but opened nothing.
                        Measured on production: the click reached the button
                        (a capture-phase listener fired), yet no [role="menu"]
                        ever appeared and no JS error was raised.

                        The cause is where stopPropagation sat. Here it was on
                        the <Menu> root; PageTree (PageTree.tsx:370) and
                        ListView (ListView.tsx:218) put it on the ActionIcon
                        INSIDE <Menu.Target>, and PageTree's menu verifiably
                        works (Rename/Duplicate/Delete renders). This now
                        matches them: stopPropagation belongs on the target
                        button, not on the Menu. */}
                    <Menu shadow="md">
                      <Menu.Target>
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          aria-label="More actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconDots size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={() => setEditingItem(item)}
                        >
                          Edit
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconCopy size={14} />}
                          onClick={() => onItemDuplicate?.(item)}
                        >
                          Duplicate
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>

                  {/* Property chips */}
                  {item.properties.length > 0 && (
                    <Group gap="xs" mt="xs">
                      {item.properties.slice(0, 3).map(prop => {
                        const propDef = database.schema.properties.find(p => p.id === prop.propertyId);
                        if (!propDef) return null;
                        return (
                          <PropertyChip
                            key={prop.propertyId}
                            property={propDef}
                            value={prop.value}
                            /* F 490 — the owner of this date, so a reminder
                               can point at the record that holds it. */
                            ownerId={item.id}
                            ownerPropertyId={prop.propertyId}
                            notificationText={item.title}
                            onChange={(value) => handlePropertyChange(item.id, prop.propertyId, value)}
                          />
                        );
                      })}
                    </Group>
                  )}
                </div>
              </Card>
            );
          })}
          </SimpleGrid>
      </Box>

      <Modal
        opened={isAddingItem}
        onClose={() => setIsAddingItem(false)}
        title="Add Item"
        size="sm"
      >
        <Stack>
          <TextInput
            placeholder="Item title"
            label="Title"
            value={newItemTitle}
            onChange={(e) => setNewItemTitle(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateItem();
            }}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setIsAddingItem(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateItem} disabled={!newItemTitle.trim()}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!editingItem}
        onClose={() => setEditingItem(null)}
        title="Edit Item"
        size="sm"
      >
        {editingItem && (
          <Stack>
            <TextInput
              label="Title"
              value={editingItem.title}
              onChange={(e) =>
                setEditingItem({ ...editingItem, title: e.currentTarget.value })
              }
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => setEditingItem(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (editingItem) {
                    onItemUpdate?.(editingItem);
                    setEditingItem(null);
                  }
                }}
              >
                Save
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
};

export default GalleryView;
